// |--------------------------------------------------------------------------
// | KEEN MAIN
// |--------------------------------------------------------------------------

import * as adapter from '@/analytics/adapters/keen-adapter'
import { datasetOptions } from '@/analytics/settings/keen-dataset-settings'
import { clientSideCache, queryOptions } from '@/analytics/settings/keen-settings'
import { setupPermissionIds } from '@/analytics/utils/data-analyser-utils'
import { getAttrType, getInterval, getTimeframe, getTimeframePublishedReadership } from '@/analytics/utils/keen-utils'
import KeenAnalysis from 'keen-analysis'

let keen

const Keen = function () {
  this.initClients()
}

// Initialize a Keen Analysis client instance (DEFAULT: PROD | ALL)
Keen.prototype.initClients = function (webProjectId = process.env.VUE_APP_KEEN_WEB_PROJECT_ID,
    webReadKey = process.env.VUE_APP_KEEN_WEB_READ_KEY,
    webMasterKey = process.env.VUE_APP_KEEN_WEB_MASTER_KEY,
    emailProjectId = process.env.VUE_APP_KEEN_EMAIL_PROJECT_ID,
    emailReadKey = process.env.VUE_APP_KEEN_EMAIL_READ_KEY,
    emailMasterKey = process.env.VUE_APP_KEEN_EMAIL_MASTER_KEY,
    emailNewsletterProjectId = process.env.VUE_APP_KEEN_EMAIL_NEWSLETTER_PROJECT_ID,
    emailNewsletterReadKey = process.env.VUE_APP_KEEN_EMAIL_NEWSLETTER_READ_KEY,
    emailNewsletterMasterKey = process.env.VUE_APP_KEEN_EMAIL_NEWSLETTER_MASTER_KEY,
    sendgridProjectId = process.env.VUE_APP_KEEN_SENDGRID_PROJECT_ID,
    sendgridReadKey = process.env.VUE_APP_KEEN_SENDGRID_READ_KEY,
    sendgridMasterKey = process.env.VUE_APP_KEEN_SENDGRID_MASTER_KEY,
    host = process.env.VUE_APP_KEEN_HOST,
) {
  this.webClient = new KeenAnalysis({
    projectId: webProjectId,
    readKey: webReadKey,
    masterKey: webMasterKey,
    cache: clientSideCache,
    host,
  })

  this.emailClient = new KeenAnalysis({
    projectId: emailProjectId,
    readKey: emailReadKey,
    masterKey: emailMasterKey,
    cache: clientSideCache,
    host,
  })

  this.emailNewsletterClient = new KeenAnalysis({
    projectId: emailNewsletterProjectId,
    readKey: emailNewsletterReadKey,
    masterKey: emailNewsletterMasterKey,
    cache: clientSideCache,
    host,
  })

  this.sendgridClient = new KeenAnalysis({
    projectId: sendgridProjectId,
    readKey: sendgridReadKey,
    masterKey: sendgridMasterKey,
    cache: clientSideCache,
    host,
  })

  this.clients = {email: this.emailClient, emailNewsletter: this.emailNewsletterClient, web: this.webClient, sendgrid: this.sendgridClient}
}

Keen.prototype.listCachedDatasets = async function (types) {
  return adapter.listCachedDatasets(this.clients, types)
}

Keen.prototype.getCachedDataset = async function (type, name) {
  try {
    const dataset = await adapter.getCachedDataset(this.clients[type], name)
    return dataset
  } catch (error) {
    // console.log('Error', error)
    return null
  }
}

Keen.prototype.createCachedDataset = async function (name, option, stream, timeframe = 'this_14_days') {
  try{

    console.log(option, stream, timeframe, this.clients, getType(name), datasetOptions)
    return adapter.createCachedDataset(this.clients[getType(name)], name, datasetOptions[option](stream, timeframe))
  } catch (error) {
    console.log('Error', error)
    return null
  }
}

Keen.prototype.deleteCachedDataset = async function (name) {
  try {
    console.log("DELETING CACHED DATASET")
    const del = await adapter.deleteCachedDataset(this.clients[getType(name)], name)
    return del
  } catch (error) {
    console.log('Error', error)
    return null
  }
}

Keen.prototype.fetchStreamAnalyses = async function (indexBy, sources, timeframeMode, id, channelId = null) {
  try {
    const timeframe = getTimeframe(timeframeMode)
    const idsArr = setupPermissionIds(indexBy, null, id)
    const fetchType = ['article', 'newsletter', 'alert'].includes(indexBy) || timeframeMode === 'ytd' ? 'single' : 'double'
    let result = await this.fetchAnalyses(null, sources, timeframe, idsArr, fetchType)

    if (!['newsletter', 'alert'].includes(indexBy)) {
      result = {...result, emailReadCategory: await this.fetchEmailReadCategoryStreamAnalyses(timeframe, idsArr, fetchType)}
    }

    if (['article', 'analyst'].includes(indexBy) && channelId !== null && channelId !== undefined) {
      const locSource = { web: sources.web.slice()}
      const locIdsArr = [{prop: 'channel', value: channelId}]
      result['channel'] = await this.fetchAnalyses(null, locSource, timeframe, locIdsArr)
      result['channel'] = {...result['channel'], emailReadCategory: await this.fetchEmailReadCategoryStreamAnalyses(timeframe, locIdsArr, fetchType)}
    }

    if (['article', 'analyst', 'account'].includes(indexBy)) {
      const locSource = { web: sources.web.slice()}
      const locIdsArr = [{prop: 'network', value: 0}]
      result['network'] = await this.fetchAnalyses(null, locSource, timeframe, locIdsArr)
      result['network'] = {...result['network'], emailReadCategory: await this.fetchEmailReadCategoryStreamAnalyses(timeframe, locIdsArr, fetchType)}
    }

    if (['newsletter'].includes(indexBy)) {
      const locSource = {sendgrid: sources.sendgrid.slice()}
      result['networkNewsletterType'] = await this.fetchAnalyses(null, locSource, timeframe, [{prop: 'networkNewsletterType', value: 0}])

      // const locSource2 = {sendgrid: sources.emailNewsletter.slice()}
      const locIdsArr = [{prop: 'network', value: 0}]

      // console.log({locSource2})

      result = {...result, emailNewsletterReadCategory: await this.fetchEmailReadCategoryStreamAnalyses(timeframe, idsArr, fetchType, locSource)}

      result['network'] = await this.fetchAnalyses(null, locSource, timeframe, locIdsArr)
      result['network'] = {...result['network'], emailNewsletterReadCategory: await this.fetchEmailReadCategoryStreamAnalyses(timeframe, locIdsArr, fetchType, locSource)}
    }

    return result
  } catch (error) {
    console.error('Error', error)
  }
}

Keen.prototype.fetchArticleReadership = async function (article) {
  try {
    const id = article.id
    const channelId = article.channel_content_type.channel.id

    const indexBy = 'article'
    const timeframe = getTimeframePublishedReadership(article)
    const idsArr = setupPermissionIds(indexBy, null, id)
    const idsArrChannel = [{prop: 'channel', value: channelId}]
    const idsArrNetwork = [{prop: 'network', value: 0}]

    const webViews = await this.fetchAnalyses(null, {web: ['article_views']}, timeframe, idsArr)
    const emailReadCategory = await this.fetchAnalyses(null, {sendgrid: ['email_open']}, timeframe, idsArr)
    console.log({emailReadCategory})

    const channelWebViews = await this.fetchAnalyses(null, {web: ['article_views']}, timeframe, idsArrChannel)
    const channelEmailReadCategory = await this.fetchAnalyses(null, {sendgrid: ['email_open']}, timeframe, idsArrChannel)

    const networkWebViews = await this.fetchAnalyses(null, {web: ['article_views']}, timeframe, idsArrNetwork)
    const networkEmailReadCategory = await this.fetchAnalyses(null, {sendgrid: ['email_open']}, timeframe, idsArrNetwork)

    return {
      sendgrid: emailReadCategory.sendgrid,
      web: webViews.web,
      channel: {
        sendgrid: channelEmailReadCategory.sendgrid,
        web: channelWebViews.web,
      },
      network: {
        sendgrid: networkEmailReadCategory.sendgrid,
        web: networkWebViews.web,
      },
    }
  } catch (error) {
    console.error('Error', error)
  }
}

Keen.prototype.fetchNewsletterReadership = async function (newsletter) {
  try {
    const id = newsletter.id || newsletter._id

    const indexBy = 'newsletter'
    const timeframe = getTimeframePublishedReadership(newsletter)
    const idsArr = setupPermissionIds(indexBy, null, id)
    const idsArrNetwork = [{prop: 'network', value: 0}]

    // const emailReadCategory = await this.fetchAnalyses('read-category', {emailNewsletter: ['email_newsletter_views']}, timeframe, idsArr)

    // const networkEmailReadCategory = await this.fetchAnalyses('read-category', {emailNewsletter: ['email_newsletter_views']}, timeframe, idsArrNetwork)

    return {
      // emailReadCategory: emailReadCategory.emailNewsletter,
      // network: {
      //   emailReadCategory: networkEmailReadCategory.emailNewsletter,
      // },
    }
  } catch (error) {
    console.error('Error', error)
  }
}

// Fetch email views read_category grouped
Keen.prototype.fetchEmailReadCategoryStreamAnalyses = async function (timeframe, idsArr, fetchType, source = null) {
  const locSource = source || {email: ['email_article_views']}

  const emailReadCategory = await this.fetchAnalyses('read-category', locSource, timeframe, idsArr, fetchType)
  const attr = Object.keys(locSource)[0]

  return emailReadCategory[attr]
}

Keen.prototype.fetchAnalyses = async function (groupBy, sources, timeframe, inputIdsArr, fetchType = 'single') {
  try {
    const result = {}
    Object.keys(sources).map((c) => {
      result[c] = {}
    })


    for (const source in sources) {
      if (!['web', 'sendgrid'].includes(source)) {
        console.log('skipping ', source)
        continue;
      }
      if (sources[source] !== undefined) {
        const options = queryOptions[source]
        const indexBy = inputIdsArr.map((obj) => obj.prop).join('_')

        const idsArr = []
        for (const obj of inputIdsArr) {
          idsArr.push({
            value: (getAttrType(source, options[getType(obj.prop)]) ? obj.value.toString() : obj.value),
            prop: options[obj.prop],
          })
        }

        for (const stream of sources[source]) {
          const params = [source, `${source}-${indexBy}${groupBy ? '-' + groupBy : ''}-${stream}-${getInterval(timeframe)}-analyses`, idsArr, timeframe]
          result[source][stream] = fetchType === 'single'
            ? await this.fetchKeenAnalyses(...params)
            : await this.fetchKeenAnalysesDouble(...params)
        }
      }
    }

    return result
  } catch (error) {
    console.error('Error', error)
  }
}

Keen.prototype.fetchKeenAnalyses = async function (source, dataset, idsArr, timeframe) {
  try {
    const resp = await adapter.queryCachedDataset(this.clients[source], dataset, idsArr, timeframe)

    const result = resp.result

    return {'current': result}
  } catch (error) {
    console.error('Error', error)
  }
}

// Double timeframe for current and previous comparison values
Keen.prototype.fetchKeenAnalysesDouble = async function (source, dataset, idsArr, timeframe) {
  try {
    const timeframeDouble = `${timeframe.split('_')[0]}_${parseInt(timeframe.split('_')[1]) * 2}_${timeframe.split('_')[2]}`
    const resp = await adapter.queryCachedDataset(this.clients[source], dataset, idsArr, timeframeDouble)

    const result = resp.result
    const half = Math.ceil(result.length / 2)
    const previous = result.slice(0, half)
    const current = result.slice(half, result.length)

    return {'previous': previous, 'current': current}
  } catch (error) {
    console.error('Error', error)
  }
}

const getType = (name) => name.split('-')[0]

export default keen = keen || new Keen()
